import dayjs from 'dayjs'
import { useMemo } from 'react'

import { DashboardIntervalStatFragment, GroupByDate } from '@/graphql'
import { filterDataPointsWithMeaningfulData } from '@/utils/dataCoverage.helpers'

export interface DataCoverageInfo {
  totalPeriods: number
  periodsWithData: number
  coveragePercentage: number
  isLowCoverage: boolean
  coverageMessage: string
}

interface UseDataCoverageProps {
  chartData?: DashboardIntervalStatFragment[]
  startDate?: string | null
  endDate?: string | null
  dataGranularity?: GroupByDate
  threshold?: number
}

export const useDataCoverage = ({
  chartData,
  startDate,
  endDate,
  dataGranularity = GroupByDate.DAY,
  threshold = 30,
}: UseDataCoverageProps): DataCoverageInfo | null => {
  return useMemo(() => {
    if (!chartData || !startDate || !endDate) {
      return null
    }

    const startDay = dayjs(startDate)
    const endDay = dayjs(endDate)

    const filteredChartData = chartData.filter(dataPoint => {
      const dataPointDate = dayjs(dataPoint.date)
      switch (dataGranularity) {
        case GroupByDate.MONTH:
          return (
            dataPointDate.isSame(startDay, 'month') ||
            dataPointDate.isSame(endDay, 'month') ||
            (dataPointDate.isAfter(startDay, 'month') &&
              dataPointDate.isBefore(endDay, 'month'))
          )
        case GroupByDate.YEAR:
          return (
            dataPointDate.isSame(startDay, 'year') ||
            dataPointDate.isSame(endDay, 'year') ||
            (dataPointDate.isAfter(startDay, 'year') &&
              dataPointDate.isBefore(endDay, 'year'))
          )
        case GroupByDate.WEEK:
          return (
            dataPointDate.isSame(startDay, 'week') ||
            dataPointDate.isSame(endDay, 'week') ||
            (dataPointDate.isAfter(startDay, 'week') &&
              dataPointDate.isBefore(endDay, 'week'))
          )
        default:
          return (
            dataPointDate.isAfter(startDay.subtract(1, 'day')) &&
            dataPointDate.isBefore(endDay.add(1, 'day'))
          )
      }
    })

    let unit: string

    switch (dataGranularity) {
      case GroupByDate.WEEK:
        unit = 'weeks'
        break
      case GroupByDate.MONTH:
        unit = 'months'
        break
      case GroupByDate.YEAR:
        unit = 'years'
        break
      default:
        unit = 'days'
    }

    const dataPointsWithData =
      filterDataPointsWithMeaningfulData(filteredChartData).length

    const coveragePercentage = Math.round(
      (dataPointsWithData / Math.max(filteredChartData.length, 1)) * 100,
    )
    const isLowCoverage = coveragePercentage < threshold

    const coverageMessage = `${dataPointsWithData} of ${filteredChartData.length} ${unit}`

    return {
      totalPeriods: filteredChartData.length,
      periodsWithData: dataPointsWithData,
      coveragePercentage,
      isLowCoverage,
      coverageMessage,
    }
  }, [chartData, startDate, endDate, dataGranularity, threshold])
}
